import { useState } from "react";
import { Link } from "react-router-dom";
import { EmojiRatingDisplay } from "@/components/EmojiRating";
import { Trash2, MessageCircle, CheckCircle2, Send, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const DIM_LABELS_PARENT = [
  ["teachers_rating", "Rapporto tra educatrici"],
  ["spaces_rating", "Spazi"],
  ["communication_rating", "Comunicazione educatrici-genitori"],
  ["food_rating", "Mensa"],
  ["activities_rating", "Attività / laboratori"],
  // legacy — mostrati solo se presenti su recensioni pre-dic 2026
  ["organization_rating", "Organizzazione"],
  ["listened_rating", "Ascolto"],
];
const DIM_LABELS_PRO = [
  ["teachers_rating", "Disponibilità verso i genitori"],
  ["spaces_rating", "Spazi"],
  ["communication_rating", "Collaborazione con le colleghe"],
  ["food_rating", "Mensa"],
  ["activities_rating", "Varietà / creatività delle attività"],
  ["organization_rating", "Organizzazione"],
  ["listened_rating", "Ascolto"],
];
const CHOOSE_LABEL = { si: "Sì, la sceglierei di nuovo", no: "No, non la sceglierei di nuovo", non_so: "Non saprei" };

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function Avatar({ name, picture, size = 36 }) {
  if (picture) {
    return <img src={picture} alt={name} className="rounded-full object-cover border border-[#E8E6E1]" style={{ width: size, height: size }} />;
  }
  return (
    <div
      className="rounded-full bg-[#DCE4DC] flex items-center justify-center font-display text-[#1A362D] font-medium"
      style={{ width: size, height: size, fontSize: size * 0.45 }}
    >
      {(name || "?")[0]}
    </div>
  );
}

/**
 * AuthorLink: avvolge avatar + nome in un Link al profilo pubblico /utente/:slug
 * SOLO se: (1) viewer è loggato (privacy-first) E (2) lo slug è disponibile.
 * Altrimenti rende il contenuto come testo normale.
 */
function AuthorLink({ slug, viewer, children, testid }) {
  if (!viewer || !slug) {
    return <>{children}</>;
  }
  return (
    <Link
      to={`/utente/${slug}`}
      data-testid={testid}
      className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity"
      title="Vedi profilo"
    >
      {children}
    </Link>
  );
}


const REPORT_ENDPOINTS = { review: "reviews", reply: "replies", product_review: "product-reviews" };

export function ReportButton({ targetType, targetId, onReported }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const askLogin = () => toast("Accedi per segnalare un contenuto ai moderatori", {
    action: {
      label: "Accedi",
      onClick: () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      },
    },
  });

  const submit = async (e) => {
    e?.preventDefault();
    if (reason.trim().length < 3) {
      toast.error("Indica un motivo (almeno 3 caratteri)");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = `/${REPORT_ENDPOINTS[targetType]}/${targetId}/report`;
      await api.post(endpoint, { reason: reason.trim() });
      toast.success("Segnalazione inviata. Sarà valutata dai moderatori.");
      setOpen(false); setReason("");
      onReported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally { setSubmitting(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => (user ? setOpen(true) : askLogin())}
        data-testid={`report-${targetType}-btn-${targetId}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E1] px-3 py-1 font-ui text-sm text-[#5C6B64] hover:border-[#B33A3A] hover:text-[#B33A3A] transition-colors"
        title="Segnala un contenuto inappropriato ai moderatori"
      >
        <Flag size={14} /> Segnala
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="mt-3 p-3 bg-[#FAF0EC] border border-[#C05C45]/30 rounded-md" data-testid={`report-form-${targetId}`}>
      <p className="label-eyebrow text-[#C05C45] mb-2">Segnalazione abuso</p>
      <textarea
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Perché ritieni questo contenuto inappropriato?"
        data-testid={`report-reason-${targetId}`}
        className="w-full border border-[#E8E6E1] rounded-md p-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none"
      />
      <div className="mt-2 flex gap-2">
        <button type="submit" disabled={submitting} data-testid={`report-submit-${targetId}`} className="btn-danger text-xs px-3 py-1.5 disabled:opacity-50">{submitting ? "Invio…" : "Invia segnalazione"}</button>
        <button type="button" onClick={() => { setOpen(false); setReason(""); }} className="btn-secondary text-xs px-3 py-1.5">Annulla</button>
      </div>
    </form>
  );
}

function ReplyItem({ reply, currentUser, onDelete }) {
  const isOwner = currentUser && reply.user_id === currentUser.user_id;
  return (
    <div className="flex gap-3" data-testid={`reply-${reply.id}`}>
      <AuthorLink slug={reply.user_slug} viewer={currentUser} testid={`reply-author-link-${reply.id}`}>
        <Avatar name={reply.user_name} picture={reply.user_picture} size={32} />
      </AuthorLink>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <AuthorLink slug={reply.user_slug} viewer={currentUser} testid={`reply-name-link-${reply.id}`}>
            <span className="font-ui font-medium text-sm text-[#1A362D] hover:text-[#C05C45] transition-colors">{reply.user_name}</span>
          </AuthorLink>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#DCE4DC] px-1.5 py-0.5 rounded">
            <CheckCircle2 size={10} /> Genitore verificato
          </span>
          <span className="text-xs text-[#8A9691]">{formatDate(reply.created_at)}</span>
          {isOwner && (
            <button onClick={() => onDelete(reply.id)} data-testid={`delete-reply-btn-${reply.id}`} className="ml-auto text-[#8A9691] hover:text-[#B33A3A] transition-colors" aria-label="Elimina risposta">
              <Trash2 size={13} />
            </button>
          )}
        </div>
        <p className="mt-1 font-ui text-sm text-[#1A362D] leading-relaxed whitespace-pre-line">{reply.comment}</p>
        {!isOwner && <div className="mt-1"><ReportButton targetType="reply" targetId={reply.id} /></div>}
      </div>
    </div>
  );
}

export function ReviewCard({ review, onDelete, onReload }) {
  const { user } = useAuth();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const replies = review.replies || [];
  const isReviewOwner = user && review.user_id === user.user_id;
  // "professionista" è il valore canonico. Accetta anche "educatrice" per retrocompat
  // con eventuali recensioni scritte prima del rename.
  const isProfessionalReview = review.reviewer_role === "professionista" || review.reviewer_role === "educatrice";
  const DIM_LABELS = isProfessionalReview ? DIM_LABELS_PRO : DIM_LABELS_PARENT;

  const submitReply = async (e) => {
    e?.preventDefault();
    if (replyText.trim().length < 2) { toast.error("Scrivi almeno 2 caratteri"); return; }
    setSubmitting(true);
    try {
      await api.post(`/reviews/${review.id}/replies`, { comment: replyText.trim() });
      setReplyText(""); setShowReplyForm(false); onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Eliminare questa risposta?")) return;
    try { await api.delete(`/replies/${rid}`); onReload?.(); } catch { toast.error("Errore"); }
  };

  return (
    <article data-testid={`review-card-${review.id}`} className="card-surface p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AuthorLink slug={review.user_slug} viewer={user} testid={`review-author-link-${review.id}`}>
            <Avatar name={review.user_name} picture={review.user_picture} size={40} />
          </AuthorLink>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <AuthorLink slug={review.user_slug} viewer={user} testid={`review-name-link-${review.id}`}>
                <p className="font-ui font-medium text-[#1A362D] hover:text-[#C05C45] transition-colors">{review.user_name}</p>
              </AuthorLink>
              {isProfessionalReview ? (
                <span
                  data-testid={`review-role-badge-professionista-${review.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C05C45] bg-[#FAF3E7] border border-[#C05C45]/25 px-1.5 py-0.5 rounded"
                  title="Recensione lasciata da un professionista dell'infanzia"
                >
                  👩‍🏫 Professionista
                </span>
              ) : (
                <span
                  data-testid={`review-role-badge-genitore-${review.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#DCE4DC] px-1.5 py-0.5 rounded"
                  title="Recensione lasciata da un genitore verificato"
                >
                  <CheckCircle2 size={10} /> Genitore verificato
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-[#8A9691] font-ui">{formatDate(review.created_at)}</span>
            </div>
          </div>
        </div>
        {onDelete && (
          <button onClick={() => onDelete(review.id)} data-testid={`delete-review-btn-${review.id}`} className="text-[#8A9691] hover:text-[#B33A3A] transition-colors" aria-label="Elimina esperienza">
            <Trash2 size={16} />
          </button>
        )}
      </header>
      {(() => {
        const dims = DIM_LABELS.filter(([k]) => review[k] != null);
        const hasChoose = review.would_choose_again && CHOOSE_LABEL[review.would_choose_again];
        if (dims.length === 0 && !hasChoose) return null;
        return (
          <div className="mt-4 pt-3 border-t border-[#E8E6E1] space-y-2" data-testid={`review-dims-${review.id}`}>
            {dims.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {dims.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 min-w-0">
                    <EmojiRatingDisplay value={review[k]} size="text-base" />
                    <span className="font-ui text-xs text-[#5C6B64] truncate">{label}</span>
                  </div>
                ))}
              </div>
            )}
            {hasChoose && (
              <p className="font-ui text-xs text-[#1A362D]">
                <span className="text-[#8A9691]">Se potesse tornare indietro:</span>{" "}
                <span className="font-medium">{CHOOSE_LABEL[review.would_choose_again]}</span>
              </p>
            )}
          </div>
        );
      })()}
      {review.comment && review.comment.trim().length > 0 && (
        <p className="mt-3 font-ui text-[#1A362D] leading-relaxed whitespace-pre-line">{review.comment}</p>
      )}

      {/* Footer actions: report (if not owner) */}
      {!isReviewOwner && (
        <div className="mt-3"><ReportButton targetType="review" targetId={review.id} /></div>
      )}

      {(replies.length > 0 || user) && (
        <div className="mt-5 pl-5 border-l border-[#E8E6E1] space-y-4">
          {replies.map((rep) => (
            <ReplyItem key={rep.id} reply={rep} currentUser={user} onDelete={deleteReply} />
          ))}
          {user && !showReplyForm && (
            <button onClick={() => setShowReplyForm(true)} data-testid={`open-reply-form-${review.id}`} className="inline-flex items-center gap-1.5 text-xs font-ui text-[#C05C45] hover:text-[#A64A35] transition-colors">
              <MessageCircle size={13} /> Rispondi
            </button>
          )}
          {user && showReplyForm && (
            <form onSubmit={submitReply} className="flex flex-col gap-2" data-testid={`reply-form-${review.id}`}>
              <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} placeholder="Scrivi una risposta…" data-testid={`reply-input-${review.id}`} className="w-full border border-[#E8E6E1] rounded-md p-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
              <div className="flex gap-2">
                <button type="submit" disabled={submitting} data-testid={`submit-reply-btn-${review.id}`} className="btn-primary text-xs inline-flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50">
                  <Send size={12} /> {submitting ? "Invio…" : "Invia"}
                </button>
                <button type="button" onClick={() => { setShowReplyForm(false); setReplyText(""); }} data-testid={`cancel-reply-btn-${review.id}`} className="btn-secondary text-xs px-3 py-1.5">Annulla</button>
              </div>
            </form>
          )}
        </div>
      )}
    </article>
  );
}
